import { AgentType, ChatDomainPolicyMode } from '@kanbu/schema/enums';
import { isValidWebsiteUrl } from '@kanbu/schema/shared';
import { useLingui } from '@lingui/react/macro';
import type { TypedFormState } from '@utima/ui-informed';
import { useFieldState } from 'informed';
import { useCallback } from 'react';

import { Card } from '@/components/card/Card';
import { SchemaFields } from '@/components/form/SchemaFields';
import { useFieldsSchema } from '@/components/form/useFieldsSchema';

export function DomainPolicyFields() {
  const { t } = useLingui();
  const typeFieldState = useFieldState('agentType');

  const validateWebsiteUrlLines = useCallback(
    (value: unknown) => {
      const entries = Array.isArray(value)
        ? value.filter((entry): entry is string => typeof entry === 'string')
        : typeof value === 'string'
          ? value
              .split('\n')
              .map(line => line.trim())
              .filter(line => line !== '')
          : [];

      if (entries.every(entry => isValidWebsiteUrl(entry))) {
        return;
      }

      return t`Enter a valid domain or URL on each line`;
    },
    [t],
  );

  const { schema } = useFieldsSchema(
    [
      {
        name: 'chatbotConfig.domainPolicy.mode',
        label: t`Embed visibility`,
        tooltip: t`Control on which websites and subpages the chatbot script may load and run. After saving, a page that already loaded the chat may keep the previous allow/deny result for up to about 10 minutes (browser session cache).`,
        type: 'select',
        options: {
          [ChatDomainPolicyMode.All]: t`All websites`,
          [ChatDomainPolicyMode.Whitelist]: t`Whitelist only`,
          [ChatDomainPolicyMode.Blacklist]: t`Blacklist`,
        },
        defaultValue: ChatDomainPolicyMode.All,
        showOptional: false,
      },
      {
        $relevant: {
          $when: ({ formState }) => {
            const mode = (
              formState as TypedFormState<{
                chatbotConfig?: {
                  domainPolicy?: { mode?: ChatDomainPolicyMode };
                };
              }>
            ).values?.chatbotConfig?.domainPolicy?.mode;

            return (
              mode === ChatDomainPolicyMode.Whitelist ||
              mode === ChatDomainPolicyMode.Blacklist
            );
          },
          $fields: [
            {
              name: 'chatbotConfig.domainPolicy.entries',
              label: t`Domains / URLs`,
              tooltip: t`One entry per line. Use example.com for the whole domain (including subdomains). Use https://example.com/blog to limit to a URL path.`,
              type: 'text',
              uiControl: 'textarea',
              uiProps: {
                rows: 6,
                placeholder:
                  'example.com\nshop.example.com\nhttps://example.com/blog',
              },
              parser: (value: unknown) => {
                if (typeof value === 'string') {
                  return value
                    .split('\n')
                    .map(line => line.trim())
                    .filter(line => line !== '');
                }

                return value;
              },
              mask: (value: unknown) => {
                if (Array.isArray(value)) {
                  return value.join('\n');
                }

                return value ?? '';
              },
              validate: validateWebsiteUrlLines,
              validateOn: 'blur-submit',
              showErrorIfTouched: true,
            },
          ],
        },
      },
    ],
    [t, validateWebsiteUrlLines],
  );

  // Embedding via the JS widget isn't relevant to voice agents
  if (typeFieldState.value === AgentType.Voice) {
    return null;
  }

  return (
    <Card title={t`Embed domains`}>
      <SchemaFields schema={schema} />
    </Card>
  );
}
